import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useApexTheme, mergeThemeOptions } from '../utils/apexTheme';
import { groupTradesByDuration } from '../utils/backtestCalculations';

const TradeDurationChart = ({ trades, height = 250 }) => {
  const { baseThemeOptions, colors } = useApexTheme();

  const { options, series } = useMemo(() => {
    if (!trades || trades.length === 0) return { options: null, series: [] };

    const durationData = groupTradesByDuration(trades);

    // Create gradient colors from light to primary
    const gradientColors = [
      '#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5',
      colors.primary || '#2196f3'
    ];

    const componentOptions = {
      chart: {
        type: 'bar',
        height,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          distributed: true,
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: 20,
        style: {
          fontSize: '11px',
          colors: [colors.text || '#304758']
        },
        formatter: (val) => val
      },
      colors: gradientColors.slice(0, durationData.length),
      legend: {
        show: false
      },
      xaxis: {
        categories: durationData.map(d => d.bucket),
        title: {
          text: 'Number of Trades'
        },
        labels: {
          formatter: (val) => Math.round(val)
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '11px'
          }
        }
      },
      tooltip: {
        y: {
          formatter: (val) => {
            const pct = ((val / trades.length) * 100).toFixed(1);
            return `${val} trades (${pct}%)`;
          }
        }
      }
    };

    return {
      options: mergeThemeOptions(baseThemeOptions, componentOptions),
      series: [{
        name: 'Trades',
        data: durationData.map(d => d.count)
      }]
    };
  }, [trades, height, baseThemeOptions, colors]);

  if (!options) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No trade data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Trade Duration Distribution</h4>
      <Chart
        options={options}
        series={series}
        type="bar"
        height={height}
      />
    </div>
  );
};

export default TradeDurationChart;
